import { z } from "zod";
import { isFlowSocket, type SocketDefinition } from "../types";

export const ACTION_INPUT_STEP_TYPE = "action_input";
export const ACTION_OUTPUT_STEP_TYPE = "action_output";
export const WORKFLOW_ACTION_STEP_TYPE = "workflow_action";

/**
 * A port on an action_input or action_output node. Persisted on the boundary
 * step's config. The `id` is stable across internal-graph rewires so callers
 * (parent workflow_action nodes) keep their edges intact when the action
 * author renames or rewires internal steps.
 */
export const actionPortSchema = z.object({
  id: z.string().min(1),
  boundStepId: z.string().min(1),
  boundSocketId: z.string().min(1),
});

export type ActionPort = z.infer<typeof actionPortSchema>;

export const actionBoundaryConfigSchema = z.object({
  ports: z.array(actionPortSchema).default([]),
});

export type ActionBoundaryConfig = z.infer<typeof actionBoundaryConfigSchema>;

type ActionGraphSocket = {
  id: string;
  name: string;
  type: string;
  required?: boolean;
  description?: string;
};

type ActionSocketOwner = {
  id: string;
  type: string;
  /** Display label for the step. Used to disambiguate port names. */
  name?: string;
  sockets: {
    inputs: ActionGraphSocket[];
    outputs: ActionGraphSocket[];
  };
  /** For boundary steps (action_input / action_output): the persisted ports. */
  config?: { ports?: ActionPort[] } | unknown;
};

export interface ActionGraphLike {
  steps: ActionSocketOwner[];
  edges: Array<{
    id: string;
    sourceStepId: string;
    sourceSocketId: string;
    targetStepId: string;
    targetSocketId: string;
  }>;
}

export interface ActionInterfaceError {
  code:
    | "missing_socket"
    | "missing_action_input"
    | "multiple_action_inputs"
    | "multiple_action_outputs"
    | "action_boundary_connection";
  message: string;
  stepId?: string;
  socketId?: string;
}

export interface WorkflowActionInterface {
  actionInputStepId?: string;
  actionOutputStepId?: string;
  inputs: SocketDefinition[];
  outputs: SocketDefinition[];
  errors: ActionInterfaceError[];
}

function findSocket(
  step: ActionSocketOwner | undefined,
  direction: "input" | "output",
  socketId: string,
): ActionGraphSocket | undefined {
  if (!step) return undefined;
  const sockets =
    direction === "input" ? step.sockets.inputs : step.sockets.outputs;
  return sockets.find((socket) => socket.id === socketId);
}

function readPorts(step: ActionSocketOwner | undefined): ActionPort[] {
  if (!step) return [];
  const parsed = actionBoundaryConfigSchema.safeParse(step.config ?? {});
  return parsed.success ? parsed.data.ports : [];
}

/**
 * Resolve display names for a list of ports against the bound sockets.
 *
 * Rules:
 * - Default name = bound socket name.
 * - On collision: append " (Bound Step Name)".
 * - On further collision: append " (n)" where n disambiguates within the
 *   same (socket name + step name) pair.
 */
function resolvePortDisplayNames(
  ports: ActionPort[],
  resolveBound: (port: ActionPort) =>
    | {
        socketName: string;
        stepName: string;
      }
    | undefined,
): Map<string, string> {
  const baseNames = new Map<string, string>();
  const baseCounts = new Map<string, number>();
  for (const port of ports) {
    const bound = resolveBound(port);
    if (!bound) continue;
    baseNames.set(port.id, bound.socketName);
    baseCounts.set(
      bound.socketName,
      (baseCounts.get(bound.socketName) ?? 0) + 1,
    );
  }

  const stepQualified = new Map<string, string>();
  const stepQualifiedCounts = new Map<string, number>();
  for (const port of ports) {
    const bound = resolveBound(port);
    if (!bound) continue;
    const base = bound.socketName;
    if ((baseCounts.get(base) ?? 0) <= 1) {
      stepQualified.set(port.id, base);
      continue;
    }
    const qualified = `${base} (${bound.stepName})`;
    stepQualified.set(port.id, qualified);
    stepQualifiedCounts.set(
      qualified,
      (stepQualifiedCounts.get(qualified) ?? 0) + 1,
    );
  }

  const finalNames = new Map<string, string>();
  const seenIndex = new Map<string, number>();
  for (const port of ports) {
    const qualified = stepQualified.get(port.id);
    if (!qualified) continue;
    if ((stepQualifiedCounts.get(qualified) ?? 0) <= 1) {
      finalNames.set(port.id, qualified);
      continue;
    }
    const next = (seenIndex.get(qualified) ?? 0) + 1;
    seenIndex.set(qualified, next);
    finalNames.set(port.id, `${qualified} (${next})`);
  }

  return finalNames;
}

export function deriveWorkflowActionInterface(
  graph: ActionGraphLike,
): WorkflowActionInterface {
  const stepsById = new Map(graph.steps.map((step) => [step.id, step]));
  const actionInputs = graph.steps.filter(
    (step) => step.type === ACTION_INPUT_STEP_TYPE,
  );
  const actionOutputs = graph.steps.filter(
    (step) => step.type === ACTION_OUTPUT_STEP_TYPE,
  );
  const errors: ActionInterfaceError[] = [];

  if (actionInputs.length === 0) {
    errors.push({
      code: "missing_action_input",
      message: "Reusable action workflows must contain one action_input step.",
    });
  } else if (actionInputs.length > 1) {
    errors.push({
      code: "multiple_action_inputs",
      message:
        "Reusable action workflows may only contain one action_input step.",
    });
  }

  if (actionOutputs.length > 1) {
    errors.push({
      code: "multiple_action_outputs",
      message:
        "Reusable action workflows may only contain one action_output step.",
    });
  }

  const actionInput = actionInputs[0];
  const actionOutput = actionOutputs[0];

  for (const edge of graph.edges) {
    if (
      isFlowSocket(edge.sourceSocketId) ||
      isFlowSocket(edge.targetSocketId)
    ) {
      continue;
    }
    const sourceStep = stepsById.get(edge.sourceStepId);
    const targetStep = stepsById.get(edge.targetStepId);
    if (
      sourceStep?.type === ACTION_INPUT_STEP_TYPE &&
      targetStep?.type === ACTION_OUTPUT_STEP_TYPE
    ) {
      errors.push({
        code: "action_boundary_connection",
        message:
          "action_input cannot connect directly to action_output. Connect boundary sockets through internal steps instead.",
        stepId: targetStep.id,
      });
    }
  }

  const inputPorts = readPorts(actionInput);
  const inputNames = resolvePortDisplayNames(inputPorts, (port) => {
    const step = stepsById.get(port.boundStepId);
    const socket = findSocket(step, "input", port.boundSocketId);
    if (!step || !socket) return undefined;
    return {
      socketName: socket.name,
      stepName: step.name ?? step.id,
    };
  });

  const inputs: SocketDefinition[] = [];
  for (const port of inputPorts) {
    const step = stepsById.get(port.boundStepId);
    const socket = findSocket(step, "input", port.boundSocketId);
    if (!step || !socket) {
      errors.push({
        code: "missing_socket",
        message: `Action input port "${port.id}" references a missing socket (${port.boundStepId}.${port.boundSocketId}).`,
        stepId: actionInput?.id,
        socketId: port.id,
      });
      continue;
    }
    inputs.push({
      id: port.id,
      name: inputNames.get(port.id) ?? socket.name,
      type: socket.type,
      required: socket.required ?? true,
      description: socket.description,
    });
  }

  const outputPorts = readPorts(actionOutput);
  const outputNames = resolvePortDisplayNames(outputPorts, (port) => {
    const step = stepsById.get(port.boundStepId);
    const socket = findSocket(step, "output", port.boundSocketId);
    if (!step || !socket) return undefined;
    return {
      socketName: socket.name,
      stepName: step.name ?? step.id,
    };
  });

  const outputs: SocketDefinition[] = [];
  for (const port of outputPorts) {
    const step = stepsById.get(port.boundStepId);
    const socket = findSocket(step, "output", port.boundSocketId);
    if (!step || !socket) {
      errors.push({
        code: "missing_socket",
        message: `Action output port "${port.id}" references a missing socket (${port.boundStepId}.${port.boundSocketId}).`,
        stepId: actionOutput?.id,
        socketId: port.id,
      });
      continue;
    }
    outputs.push({
      id: port.id,
      name: outputNames.get(port.id) ?? socket.name,
      type: socket.type,
      required: false,
      description: socket.description,
    });
  }

  return {
    actionInputStepId: actionInput?.id,
    actionOutputStepId: actionOutput?.id,
    inputs,
    outputs,
    errors,
  };
}

export function createWorkflowActionSockets(
  actionInterface: Pick<WorkflowActionInterface, "inputs" | "outputs">,
): { inputs: SocketDefinition[]; outputs: SocketDefinition[] } {
  return {
    inputs: actionInterface.inputs.map((socket) => ({ ...socket })),
    outputs: actionInterface.outputs.map((socket) => ({ ...socket })),
  };
}

export function isActionBoundaryStepType(stepType: string): boolean {
  return (
    stepType === ACTION_INPUT_STEP_TYPE || stepType === ACTION_OUTPUT_STEP_TYPE
  );
}

/**
 * After cloning workflow steps (e.g. when creating a draft), each step gets a
 * new ID. Edges are remapped via the stepIdMap, but action_input/action_output
 * ports contain `boundStepId` references that also need remapping.
 *
 * Mutates nothing — returns a new array with updated configs where needed.
 */
export function remapActionBoundaryPorts<
  T extends { config: Record<string, unknown> },
>(
  steps: (T & { type: string })[],
  stepIdMap: Map<string, string>,
): (T & { type: string })[];
export function remapActionBoundaryPorts<
  T extends { config: Record<string, unknown> },
>(
  steps: T[],
  stepIdMap: Map<string, string>,
  getType: (step: T) => string,
): T[];
export function remapActionBoundaryPorts<
  T extends { config: Record<string, unknown> },
>(
  steps: T[],
  stepIdMap: Map<string, string>,
  getType?: (step: T) => string,
): T[] {
  const resolveType = getType ?? ((s: T) => (s as T & { type: string }).type);
  return steps.map((step) => {
    if (!isActionBoundaryStepType(resolveType(step))) return step;

    const parsed = actionBoundaryConfigSchema.safeParse(step.config ?? {});
    if (!parsed.success || parsed.data.ports.length === 0) return step;

    const remappedPorts = parsed.data.ports.map((port) => ({
      ...port,
      boundStepId: stepIdMap.get(port.boundStepId) ?? port.boundStepId,
    }));

    return {
      ...step,
      config: { ...step.config, ports: remappedPorts },
    };
  });
}
